import type {
  IntelligenceFeedItem,
  SecondOrderDomainKey,
  Storyline,
} from "@akem/shared";
import { getApiEndpoint } from "../config/api.js";

interface IntelligenceFeedOptions {
  section?: SecondOrderDomainKey | null;
  limit?: number;
  signal?: AbortSignal;
  ifNoneMatch?: string | null;
  // When true, request only storyline-derived items
  onlyStorylines?: boolean;
}

export interface IntelligenceFeedResult {
  items: IntelligenceFeedItem[];
  etag: string | null;
  notModified?: boolean;
}

export async function getIntelligenceFeed(
  instrumentId: string,
  options?: IntelligenceFeedOptions
): Promise<IntelligenceFeedResult> {
  const url = new URL(`${getApiEndpoint()}/intelligence/feed`);
  url.searchParams.set("instrumentId", instrumentId);
  if (options?.section) url.searchParams.set("section", options.section);
  if (options?.limit)
    url.searchParams.set("limit", String(Math.max(1, options.limit)));
  if (options?.onlyStorylines) url.searchParams.set("only_storylines", "true");

  const headers = new Headers();
  if (options?.ifNoneMatch) {
    headers.set("If-None-Match", options.ifNoneMatch);
  }

  const res = await fetch(url.toString(), { signal: options?.signal, headers });
  if (res.status === 304) {
    const etag = res.headers.get("etag") ?? options?.ifNoneMatch ?? null;
    return { items: [], etag, notModified: true };
  }
  if (!res.ok) throw new Error(`feed request failed: ${res.status}`);
  const json = await res.json();
  const items = Array.isArray(json?.items)
    ? (json.items as IntelligenceFeedItem[])
    : [];
  const etag = res.headers.get("etag");
  return { items, etag: etag ?? null };
}

export async function getStorylineById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<Storyline | null> {
  if (!id) return null;
  const res = await fetch(
    `${getApiEndpoint()}/intelligence/storylines/${encodeURIComponent(id)}`,
    {
      signal: options?.signal,
    }
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`storyline fetch failed: ${res.status}`);
  const json = await res.json();
  return (json?.storyline as Storyline) ?? null;
}

export async function getAllStorylines(options?: {
  limit?: number;
  domain?: string;
  signal?: AbortSignal;
}): Promise<{ items: IntelligenceFeedItem[]; etag: string | null }> {
  const url = new URL(`${getApiEndpoint()}/intelligence/storylines`);
  if (options?.limit) {
    url.searchParams.set(
      "limit",
      String(Math.max(1, Math.min(options.limit, 100)))
    );
  }
  if (options?.domain) {
    url.searchParams.set("domain", options.domain);
  }

  const res = await fetch(url.toString(), { signal: options?.signal });
  if (!res.ok)
    throw new Error(`getAllStorylines request failed: ${res.status}`);

  const json = await res.json();
  const items = Array.isArray(json?.items)
    ? (json.items as IntelligenceFeedItem[])
    : [];
  const etag = res.headers.get("etag");

  return { items, etag: etag ?? null };
}
